import type { ReactNode } from "react";
import { cn } from "./cn";

export type Tone = "green" | "red" | "amber" | "blue" | "violet" | "gray";

/**
 * Tones carry meaning, so they are named by intent at the call site rather than by colour value.
 * The inset ring keeps a badge legible on both the card surface and a hovered table row, where a
 * plain tinted background can lose its edge.
 */
const TONES: Record<Tone, string> = {
  green: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  red: "bg-red-50 text-red-700 ring-red-600/20",
  amber: "bg-amber-50 text-amber-700 ring-amber-600/20",
  blue: "bg-brand-50 text-brand-700 ring-brand-600/20",
  /* A category colour rather than a judgement: epics and code review are neither
     good nor bad, and giving them green or amber would imply one. */
  violet: "bg-violet-50 text-violet-700 ring-violet-600/20",
  gray: "bg-ink-100 text-ink-600 ring-ink-500/20",
};

export function StatusBadge({ tone = "gray", children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
        TONES[tone],
      )}
    >
      {children}
    </span>
  );
}
